import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { DollarSign, AlertCircle, Check, Loader, Trophy, Percent, Wallet } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

const AdminEconomy = () => {
  const { locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [treasuryFunds, setTreasuryFunds] = useState([]);
  const [prizePoolsSummary, setPrizePoolsSummary] = useState({ monthly: 0, seasonal: 0, annual: 0 });
  const [rankingPeriods, setRankingPeriods] = useState(null);
  const [closingRanking, setClosingRanking] = useState('');
  const [gameweeks, setGameweeks] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [allocationType, setAllocationType] = useState('gameweek');
  const [allocationForm, setAllocationForm] = useState({
    gameweekId: '',
    tournamentId: '',
    amount: '',
    description: ''
  });

  useEffect(() => {
    loadTreasuryFunds();
    loadPrizePoolsSummary();
    loadRankingPeriods();
    loadGameweeks();
  }, []);

  useEffect(() => {
    if (allocationForm.gameweekId) {
      loadGameweekTournaments(allocationForm.gameweekId);
    } else {
      setTournaments([]);
    }
  }, [allocationForm.gameweekId]);

  const loadTreasuryFunds = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getTreasuryFunds();
      if (response?.success) {
        setTreasuryFunds(response.funds || []);
      }
    } catch (err) {
      console.error('Error loading treasury funds:', err);
      showMessage('error', err.message || 'Failed to load treasury funds');
    } finally {
      setLoading(false);
    }
  };

  const loadGameweeks = async () => {
    try {
      const response = await adminApi.getGameWeeks();
      if (response?.success) {
        setGameweeks(response.gameweeks || []);
      }
    } catch (err) {
      console.error('Error loading gameweeks:', err);
    }
  };

  const loadPrizePoolsSummary = async () => {
    try {
      const response = await adminApi.getPrizePoolsSummary();
      if (response?.success) {
        setPrizePoolsSummary(response.summary || { monthly: 0, seasonal: 0, annual: 0 });
      }
    } catch (err) {
      console.error('Error loading prize pools summary:', err);
    }
  };

  const loadRankingPeriods = async () => {
    try {
      const response = await adminApi.getRankingPeriods();
      if (response?.success) {
        setRankingPeriods(response.periods || null);
      }
    } catch (err) {
      console.error('Error loading ranking periods:', err);
    }
  };

  const handleCloseRanking = async (type) => {
    if (!window.confirm(`Confermi la chiusura della classifica ${type}?`)) {
      return;
    }
    setClosingRanking(type);
    try {
      const response = await adminApi.closeRankingPeriod(type);
      if (response?.success) {
        const updated = response.result?.updated ?? 0;
        showMessage('success', `Classifica ${type} ricalcolata (${updated} tornei).`);
      } else {
        showMessage('error', response?.error || `Chiusura ${type} fallita`);
      }
    } catch (err) {
      console.error('Error closing ranking:', err);
      showMessage('error', err.response?.data?.error || err.message || `Chiusura ${type} fallita`);
    } finally {
      setClosingRanking('');
    }
  };

  const loadGameweekTournaments = async (gameweekId) => {
    try {
      const response = await adminApi.getGameWeek(parseInt(gameweekId, 10));
      if (response?.success) {
        setTournaments(response.tournaments || []);
      }
    } catch (err) {
      console.error('Error loading gameweek tournaments:', err);
      setTournaments([]);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAllocatePrize = async (e) => {
    e.preventDefault();

    if (!allocationForm.amount || !allocationForm.gameweekId) {
      showMessage('error', 'Select a gameweek and amount');
      return;
    }

    if (allocationType === 'tournament' && !allocationForm.tournamentId) {
      showMessage('error', 'Select a tournament');
      return;
    }

    const amount = parseFloat(allocationForm.amount);
    if (isNaN(amount) || amount <= 0) {
      showMessage('error', 'Amount must be greater than 0');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        amount,
        gameweekId: allocationForm.gameweekId,
        tournamentId: allocationType === 'tournament' ? allocationForm.tournamentId : undefined,
        description: allocationForm.description
      };
      const response = await adminApi.allocateGuaranteedPrize(payload);
      if (response?.success) {
        showMessage('success', 'Guaranteed prize allocated successfully');
        setAllocationForm({ gameweekId: '', tournamentId: '', amount: '', description: '' });
        setAllocationType('gameweek');
        loadTreasuryFunds();
      } else {
        showMessage('error', response?.error || 'Failed to allocate prize');
      }
    } catch (err) {
      console.error('Error allocating prize:', err);
      showMessage('error', err.response?.data?.error || err.message || 'Failed to allocate prize');
    } finally {
      setLoading(false);
    }
  };

  const getFundTotal = (type) => {
    return treasuryFunds
      .filter((fund) => fund.fund_type === type)
      .reduce((sum, fund) => sum + parseFloat(fund.amount || 0), 0);
  };

  const companyBalance = getFundTotal('company');
  const monthlyBalance = prizePoolsSummary.monthly || 0;
  const seasonalBalance = prizePoolsSummary.seasonal || 0;
  const annualBalance = prizePoolsSummary.annual || 0;
  const selectedGameweek = gameweeks.find((gw) => String(gw.id) === String(allocationForm.gameweekId));
  const selectedGameweekGuaranteed = selectedGameweek?.tournaments?.reduce((sum, t) => {
    return sum + (parseFloat(t.guaranteed_prize || 0) || 0);
  }, 0) || 0;
  const guaranteedByGameweek = gameweeks
    .map((gw) => {
      const totalGuaranteed = (gw.tournaments || []).reduce((sum, t) => {
        return sum + (parseFloat(t.guaranteed_prize || 0) || 0);
      }, 0);
      return {
        id: gw.id,
        name: gw.name,
        status: gw.status,
        startDate: gw.startDate,
        totalGuaranteed
      };
    })
    .filter((gw) => gw.totalGuaranteed > 0)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <DollarSign className="w-8 h-8 mr-3 text-green-400" />
          Economy
        </h1>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-900/50 border-green-700 text-green-200' 
            : 'bg-red-900/50 border-red-700 text-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <Check className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className="flex space-x-2 mb-6 border-b border-gray-700">
        {[
          { id: 'overview', label: 'Overview', icon: DollarSign },
          { id: 'allocate', label: 'Guaranteed Prize', icon: Trophy }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-gray-400 hover:text-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <Loader className="w-8 h-8 animate-spin mx-auto text-indigo-400" />
              <p className="mt-4 text-gray-400">Loading economy data...</p>
            </div>
          ) : (
            <>
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Wallet className="w-5 h-5 mr-2 text-green-400" />
                  Treasury Overview
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-sm text-gray-400 mb-1">Company Treasury</div>
                    <div className="text-2xl font-bold text-green-400">{companyBalance.toFixed(2)} DIOS</div>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-sm text-gray-400 mb-1">Monthly Prize Pools</div>
                    <div className="text-2xl font-bold text-blue-400">{monthlyBalance.toFixed(2)} DIOS</div>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-sm text-gray-400 mb-1">Seasonal Prize Pools</div>
                    <div className="text-2xl font-bold text-purple-400">{seasonalBalance.toFixed(2)} DIOS</div>
                  </div>
                  <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                    <div className="text-sm text-gray-400 mb-1">Annual Prize Pools</div>
                    <div className="text-2xl font-bold text-yellow-400">{annualBalance.toFixed(2)} DIOS</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Percent className="w-5 h-5 mr-2 text-indigo-400" />
                  Money Flow Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-300">
                  <div className="space-y-2">
                    <div className="font-semibold text-gray-200">Primary Market (packs/auctions)</div>
                    <div>40% → gameweek prize pools (50% categories, 50% monthly)</div>
                    <div>15% → referrals (8/5/2)</div>
                    <div>45% → company treasury</div>
                    <div className="text-xs text-gray-500">If no upcoming gameweek: 85% to company + 15% referrals</div>
                  </div>
                  <div className="space-y-2">
                    <div className="font-semibold text-gray-200">Marketplace Fees (secondary)</div>
                    <div>Fee = 10% (paid by seller/loaner)</div>
                    <div>85% → company split: 50% seasonal/annual prizes, 50% treasury</div>
                    <div>15% → referrals (8/5/2)</div>
                    <div className="font-semibold text-gray-200 mt-3">Gameweek Buy-in</div>
                    <div>90% → tournament prize pool</div>
                    <div>10% → company (referrals 8/5/2 deducted from this 10%)</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
                  Close Rankings
                </h2>
                {rankingPeriods ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-300 font-semibold mb-2">Monthly</div>
                      <div className="text-gray-400 text-xs mb-3">
                        {new Date(rankingPeriods.monthly.startDate).toLocaleDateString(locale || 'it-IT')} - {new Date(rankingPeriods.monthly.endDate).toLocaleDateString(locale || 'it-IT')}
                      </div>
                      <button
                        onClick={() => handleCloseRanking('monthly')}
                        disabled={closingRanking === 'monthly'}
                        className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm disabled:opacity-50"
                      >
                        {closingRanking === 'monthly' ? 'Closing...' : 'Close Monthly'}
                      </button>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-300 font-semibold mb-2">Semestral</div>
                      <div className="text-gray-400 text-xs mb-3">
                        {new Date(rankingPeriods.semestral.startDate).toLocaleDateString(locale || 'it-IT')} - {new Date(rankingPeriods.semestral.endDate).toLocaleDateString(locale || 'it-IT')}
                      </div>
                      <button
                        onClick={() => handleCloseRanking('semestral')}
                        disabled={closingRanking === 'semestral'}
                        className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm disabled:opacity-50"
                      >
                        {closingRanking === 'semestral' ? 'Closing...' : 'Close Semestral'}
                      </button>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-300 font-semibold mb-2">Annual</div>
                      <div className="text-gray-400 text-xs mb-3">
                        {new Date(rankingPeriods.annual.startDate).toLocaleDateString(locale || 'it-IT')} - {new Date(rankingPeriods.annual.endDate).toLocaleDateString(locale || 'it-IT')}
                      </div>
                      <button
                        onClick={() => handleCloseRanking('annual')}
                        disabled={closingRanking === 'annual'}
                        className="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-sm disabled:opacity-50"
                      >
                        {closingRanking === 'annual' ? 'Closing...' : 'Close Annual'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">Loading periods...</div>
                )}
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
                  Guaranteed Prize Distribution
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
                  {[
                    { label: 'Basic', value: '0%' },
                    { label: 'Plus', value: '13%' },
                    { label: 'Rare', value: '22%' },
                    { label: 'Epic', value: '30%' },
                    { label: 'Uniche', value: '35%' }
                  ].map((item) => (
                    <div key={item.label} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                      <div className="text-gray-400 text-xs mb-1">{item.label}</div>
                      <div className="text-lg font-bold text-white">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-indigo-400" />
                  Guaranteed Prize by Gameweek
                </h2>
                {guaranteedByGameweek.length === 0 ? (
                  <p className="text-sm text-gray-400">No guaranteed prizes assigned yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-gray-400">
                        <tr>
                          <th className="text-left py-2">Gameweek</th>
                          <th className="text-left py-2">Status</th>
                          <th className="text-left py-2">Start</th>
                          <th className="text-right py-2">Guaranteed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {guaranteedByGameweek.map((gw) => (
                          <tr key={gw.id} className="text-gray-200">
                            <td className="py-2">
                              <div className="font-semibold">{gw.name || `Gameweek ${gw.id}`}</div>
                              <div className="text-xs text-gray-500">ID: {gw.id}</div>
                            </td>
                            <td className="py-2 capitalize text-gray-300">{gw.status}</td>
                            <td className="py-2 text-gray-300">
                              {gw.startDate ? new Date(gw.startDate).toLocaleDateString(locale || 'it-IT') : 'N/A'}
                            </td>
                            <td className="py-2 text-right text-yellow-400 font-semibold">
                              {gw.totalGuaranteed.toFixed(2)} DIOS
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'allocate' && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-semibold mb-6 flex items-center">
            <Trophy className="w-5 h-5 mr-2 text-yellow-400" />
            Allocate Guaranteed Prize
          </h2>

          <form onSubmit={handleAllocatePrize} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Allocation Type
                </label>
                <select
                  value={allocationType}
                  onChange={(e) => {
                    setAllocationType(e.target.value);
                    setAllocationForm((prev) => ({ ...prev, tournamentId: '' }));
                  }}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="gameweek">Entire Gameweek (standard split)</option>
                  <option value="tournament">Single Tournament</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Gameweek <span className="text-red-400">*</span>
                </label>
                <select
                  value={allocationForm.gameweekId}
                  onChange={(e) => setAllocationForm({ ...allocationForm, gameweekId: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select gameweek...</option>
                  {gameweeks.map((gw) => (
                    <option key={gw.id} value={gw.id}>
                      {gw.name || `Gameweek ${gw.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {allocationForm.gameweekId && (
              <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 text-sm text-gray-300">
                <div className="flex items-center justify-between">
                  <span>Current guaranteed prize (gameweek)</span>
                  <span className="text-yellow-400 font-semibold">{selectedGameweekGuaranteed.toFixed(2)} DIOS</span>
                </div>
              </div>
            )}

            {allocationType === 'tournament' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Tournament <span className="text-red-400">*</span>
                </label>
                <select
                  value={allocationForm.tournamentId}
                  onChange={(e) => setAllocationForm({ ...allocationForm, tournamentId: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select tournament...</option>
                  {tournaments.map((tournament) => (
                    <option key={tournament.id} value={tournament.id}>
                      {tournament.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Amount (DIOS) <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={allocationForm.amount}
                onChange={(e) => setAllocationForm({ ...allocationForm, amount: e.target.value })}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter amount"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={allocationForm.description}
                onChange={(e) => setAllocationForm({ ...allocationForm, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Optional note for this allocation"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Allocating...
                  </>
                ) : (
                  <>
                    <Trophy className="w-5 h-5 mr-2" />
                    Allocate Prize
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminEconomy;
